import { useEffect, useRef, useState } from 'react';
import type { LutCard } from '@/lib/video-effects/lutCards';
import { createNativeEffectRenderer } from '@/lib/video-effects/nativeEffectRenderer';
import { renderLutThumbnails } from '@/lib/video-effects/renderLutThumbnails';

/** 2× the 96 px card width, 16:9 like the extracted frame. */
const THUMB_WIDTH = 192;
const THUMB_HEIGHT = 108;

/** Renders the clip frame (a data URL) through every LUT card with the
 *  preview's own WebGL renderer. Re-renders only when the frame or the
 *  imported LUT changes — picking a card does not. Any failure (no WebGL2,
 *  undecodable frame) leaves the cards on their swatches. */
export function useLutThumbnails(frameUrl: string | null | undefined, cards: LutCard[]): Record<string, string> {
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const cardsRef = useRef(cards);
  cardsRef.current = cards;
  const custom = cards.find((card) => card.kind === 'custom')?.lut ?? null;
  const renderKey = `${frameUrl ?? ''}|${custom?.name ?? ''}|${custom?.data?.length ?? 0}`;

  useEffect(() => {
    if (!frameUrl) {
      setThumbnails({});
      return undefined;
    }
    let cancelled = false;
    const image = new Image();
    image.src = frameUrl;
    image.decode().then(() => {
      if (cancelled) return;
      const output = document.createElement('canvas');
      let renderer: ReturnType<typeof createNativeEffectRenderer> | null = null;
      try {
        renderer = createNativeEffectRenderer(output);
        const next = renderLutThumbnails({
          source: image,
          cards: cardsRef.current,
          width: THUMB_WIDTH,
          height: THUMB_HEIGHT,
          renderer,
          snapshot: () => output.toDataURL('image/jpeg', 0.82),
        });
        if (!cancelled) setThumbnails(next);
      } catch {
        if (!cancelled) setThumbnails({});
      } finally {
        renderer?.dispose();
      }
    }).catch(() => {
      if (!cancelled) setThumbnails({});
    });
    return () => { cancelled = true; };
    // renderKey captures the frame and the imported LUT; cards are read via ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderKey]);

  return thumbnails;
}
